import { config } from '../config';
import { tokenStorage } from './tokenStorage';
import { useAuthStore } from './authStore';

// Single shared refresh path for the whole cabin app. Both the HTTP client and
// the realtime WebSocket call THIS function, so a single in-flight promise
// coalesces concurrent refreshes (e.g. an API 401 and a WS 4401 firing at the
// same 15-minute token expiry) into one request. Previously each path had its
// own guard, so they would submit the same refresh token twice and trip the
// server's reuse detection — logging the user out of the cabin *and* the phone.
let refreshInFlight: Promise<string | null> | null = null;

export function refreshAccessToken(): Promise<string | null> {
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = (async () => {
    const refresh = tokenStorage.getRefresh();
    if (!refresh) {
      // Nothing to refresh with — terminal.
      useAuthStore.getState().signOut();
      return null;
    }
    try {
      const resp = await fetch(`${config.apiBaseUrl}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: refresh }),
      });
      if (!resp.ok) {
        // Only a definitive auth rejection (401) ends the session. Transient
        // failures (5xx, etc.) leave tokens intact so a later retry recovers —
        // a blip must not log the cabin out.
        if (resp.status === 401) useAuthStore.getState().signOut();
        return null;
      }
      const data = (await resp.json()) as { access_token: string; refresh_token: string };
      tokenStorage.setAccess(data.access_token);
      tokenStorage.setRefresh(data.refresh_token);
      return data.access_token;
    } catch {
      // Network error — don't sign out; let the caller retry.
      return null;
    } finally {
      refreshInFlight = null;
    }
  })();
  return refreshInFlight;
}
